import { lazy, type ComponentType, type LazyExoticComponent } from "react";

/**
 * When the tab last reloaded itself for a chunk that would not load. One key
 * for every lazy component, so two failing chunks still cost one reload.
 */
export const CHUNK_RELOAD_KEY = "compaire_chunk_reload_at";

/** A second failure within this window is a real error, not a stale tab. */
export const CHUNK_RELOAD_WINDOW_MS = 60_000;

interface Options {
  /** Test seam. Defaults to `window.location.reload()`. */
  reload?: () => void;
  /** Test seam. Defaults to `window.sessionStorage`. */
  storage?: Pick<Storage, "getItem" | "setItem"> | null;
  /** Test seam. Defaults to `Date.now`. */
  now?: () => number;
}

const defaultStorage = (): Pick<Storage, "getItem" | "setItem"> | null => {
  try {
    return typeof window === "undefined" ? null : window.sessionStorage;
  } catch {
    return null;
  }
};

/**
 * `React.lazy`, plus one full reload when the chunk cannot be fetched.
 *
 * Every merge to `dev` (and every promotion to `main`) is a new Vercel
 * deployment whose hashed chunks replace the old ones. A tab opened before it
 * still asks for the old file names; Vercel no longer has them, and the
 * catch-all rewrite in vercel.json answers with `/app.html`, so the dynamic
 * import rejects (a MIME/"Failed to fetch dynamically imported module" error).
 * `React.lazy` rethrows that during render, and only the top-level boundary in
 * main.tsx catches it: "Something went wrong." A reload fetches the current
 * app.html and its chunks, which is why reloading always fixed it.
 *
 * So: on a failed import, reload once and stay suspended until the page goes.
 * The reload time is kept in sessionStorage; a failure within
 * CHUNK_RELOAD_WINDOW_MS of the last reload (or with no storage to remember
 * it) is rethrown, so a chunk that is genuinely broken can never loop.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- same constraint as React.lazy
export function lazyWithReload<T extends ComponentType<any>>(
  importer: () => Promise<{ default: T }>,
  options: Options = {},
): LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      return await importer();
    } catch (err) {
      const storage = options.storage === undefined ? defaultStorage() : options.storage;
      const now = (options.now ?? Date.now)();
      let last = NaN;
      try {
        last = Number(storage?.getItem(CHUNK_RELOAD_KEY));
      } catch {
        /* unreadable storage: treated as "no record", and then refused below */
      }
      const recentlyReloaded = Number.isFinite(last) && last > 0 && now - last < CHUNK_RELOAD_WINDOW_MS;
      let recorded = false;
      if (storage && !recentlyReloaded) {
        try {
          storage.setItem(CHUNK_RELOAD_KEY, String(now));
          recorded = true;
        } catch {
          recorded = false;
        }
      }
      // No record of this attempt means no loop guard: surface the error.
      if (!recorded) throw err;
      (options.reload ?? (() => window.location.reload()))();
      // Stay suspended (the Suspense fallback shows) until the page unloads.
      return new Promise<{ default: T }>(() => {});
    }
  });
}
